import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";

import { askPermission, formatDate, subscribeToAudioLevel } from "utils";

import TroubleshootMicModal from "./TroubleshootMicModal";

const NEVER_EXPIRY_YEAR = 9999;

/**
 * Instruction sets keyed by template type — copied verbatim from the desktop
 * `StartAssessmentDialog` so mobile shows the exact same wording.
 */
function getInstructions(template) {
  const attemptsText =
    template?.maxAllowedAttempts > 0
      ? `${template.maxAllowedAttempts} times.`
      : "as many times you want.";

  const instructions = {
    assignment: [
      <>
        You <b>can only attempt this assessment once</b>
      </>,
      <>
        You will have a certain amount of time to complete each question, so
        manage your time carefully.
      </>,
      <>
        If you don&apos;t want to attempt this assessment now, click on
        &apos;x&apos; icon on the top right corner.
      </>,
    ],
    practice: [
      <>
        This is a <b>practice</b> assessment, you can attempt it{" "}
        <b>{attemptsText}</b>
      </>,
      <>
        You will have a certain amount of time to complete each question, so
        manage your time carefully.
      </>,
      <>
        If you don&apos;t want to attempt this assessment now, click on
        &apos;x&apos; icon on the top right corner.
      </>,
    ],
    follow_up: [
      <>
        This is a <b>follow up</b> assessment, you can attempt it{" "}
        <b>{attemptsText}</b>
      </>,
      <>
        You will have a certain amount of time to complete each question, so
        manage your time carefully.
      </>,
      <>
        If you don&apos;t want to attempt this assessment now, click on
        &apos;x&apos; icon on the top right corner.
      </>,
    ],
    pathway: [
      <>
        This is a <b>pathway</b> assessment, you can attempt it{" "}
        <b>{attemptsText}</b>
      </>,
      <>
        You will have a certain amount of time to complete each question, so
        manage your time carefully.
      </>,
      <>
        If you don&apos;t want to attempt this assessment now, click on
        &apos;x&apos; icon on the top right corner.
      </>,
    ],
  };

  return instructions[template?.type] || instructions.practice;
}

// Maximum Time Allowed — same formatting as the desktop dialog.
function getTimeLabel(template) {
  const testTime = template?.metadata?.testTime;
  if (!testTime) return "5-10 mins";
  return testTime > 60 ? `${Math.ceil(testTime / 60)} minutes` : `${testTime} secs`;
}

function getDeadlineLabel(template) {
  const { expiresOn } = template || {};
  if (expiresOn && dayjs(expiresOn).year() !== NEVER_EXPIRY_YEAR) {
    return formatDate(expiresOn, "DD MMMM, hA");
  }
  return "Never expires";
}

function InfoRow({ dotColor, label, value, valueColor = "neutral.clr-700" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: dotColor,
          mt: "6px",
          flexShrink: 0,
        }}
      />
      <Typography variant="body02-medium" color="neutral.clr-700">
        <Box component="span" sx={{ fontWeight: 700 }}>
          {label}:{" "}
        </Box>
        <Box component="span" sx={{ color: valueColor }}>
          {value}
        </Box>
      </Typography>
    </Box>
  );
}

/**
 * Interview instructions modal (mobile) — the mobile equivalent of the desktop
 * `StartAssessmentDialog` instructions view. Opened when a template card is
 * tapped on the dashboard.
 *
 * @param {boolean} open
 * @param {object}  template   raw todo template (from getTodoTemplates)
 * @param {()=>void} onClose   close the modal (X / backdrop)
 * @param {()=>void} onStart   "Let's Go" — proceed to the next screen
 */
export default function InterviewInstructionsModal({
  open = false,
  template,
  onClose = () => {},
  onStart = () => {},
}) {
  // Mic calibration + permission gate — mobile port of the desktop
  // `StartAssessmentDialog` behaviour. "Let's Go" stays disabled until we detect
  // a working mic (permState === "GRANTED"); the level bar shows live input.
  const [permState, setPermState] = React.useState("INITIAL");
  const [audioLevel, setAudioLevel] = React.useState(0);
  const [troubleshootOpen, setTroubleshootOpen] = React.useState(false);
  const unsubscribeRef = React.useRef(null);

  const reset = React.useCallback(() => {
    if (typeof unsubscribeRef.current === "function") {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  const subscribe = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      unsubscribeRef.current = subscribeToAudioLevel(stream, setAudioLevel);
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Ask for mic (gates the flow) + camera (best-effort, like desktop). On iOS
  // Safari `navigator.permissions` is unreliable, so we rely on the
  // getUserMedia result via `askPermission` rather than `checkPermission`.
  const requestPermissions = React.useCallback(async () => {
    const audioOk = await askPermission({ audio: true });
    askPermission({ video: true }).catch(() => {});

    if (audioOk) {
      subscribe();
      setPermState("GRANTED");
    } else {
      setPermState("ASK_PERMISSION");
    }
  }, [subscribe]);

  React.useEffect(() => {
    if (open) {
      setPermState("INITIAL");
      requestPermissions();
    }
    // Release the mic stream when the modal closes / unmounts (e.g. on
    // "Let's Go", the parent clears the template and this component unmounts).
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!template) return null;

  const name =
    template?.type === "personalized"
      ? template?.data?.INTERVIEW_DETAILS?.name
      : template?.name;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: 3,
          // Bound the paper and lay it out as a column so only the middle
          // content scrolls — the header + footer stay put.
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100% - 48px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 3,
        }}
      >
        <Typography variant="h6-semiBold" color="neutral.clr-900">
          Assessment
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: "neutral.clr-800", p: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Content — the only scrollable region when it overflows. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 4,
          py: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <InfoRow
          dotColor="primary.clr-300"
          label="Assessment"
          value={name}
          valueColor="primary.clr-300"
        />
        <InfoRow
          dotColor="primary.clr-300"
          label="Maximum Time Allowed"
          value={getTimeLabel(template)}
        />
        <InfoRow
          dotColor="warning.clr-300"
          label="Deadline"
          value={getDeadlineLabel(template)}
        />

        <Typography variant="body01-bold" color="neutral.clr-900" sx={{ mt: 1 }}>
          General Instructions
        </Typography>

        <Box
          component="ul"
          sx={{
            pl: 5,
            m: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {getInstructions(template).map((instruction, i) => (
            <Typography
              key={i}
              component="li"
              variant="body02-regular"
              color="neutral.clr-700"
            >
              {instruction}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "warning.clr-300",
            bgcolor: "warning.clr-100",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography
            variant="body02-semiBold"
            color="warning.clr-500"
            align="center"
            sx={{ display: "block" }}
          >
            Remember to sit in a noise free environment
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Footer */}
      <Box
        sx={{
          flexShrink: 0,
          px: 4,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Ready + Let's Go — gated on mic permission. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body01-regular" color="neutral.clr-700">
            Ready to start?
          </Typography>
          <Button
            variant="contained"
            disabled={permState !== "GRANTED"}
            onClick={onStart}
            sx={{ px: 5, borderRadius: 2 }}
          >
            Let&apos;s Go
          </Button>
        </Box>

        {/* Mic calibration — live input level (linear bar, as desktop). */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <MicIcon
            sx={{
              fontSize: 18,
              color: permState === "GRANTED" ? "primary.clr-300" : "neutral.clr-400",
            }}
          />
          <Box sx={{ width: 120 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, audioLevel)}
              sx={{ borderRadius: 10, height: 6 }}
            />
          </Box>
        </Box>

        {/* Blocked-permission state. */}
        {permState === "ASK_PERMISSION" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="body02-medium"
              color="danger.clr-700"
              align="center"
            >
              It seems you have blocked mic or camera permissions!
            </Typography>
            <Button
              variant="contained"
              onClick={requestPermissions}
              sx={{ px: 5, borderRadius: 2 }}
            >
              Give Permission
            </Button>
          </Box>
        )}

        {/* Troubleshoot. */}
        <Typography
          variant="body02-semiBold"
          color="neutral.clr-700"
          align="center"
        >
          Facing any issue with mic?{" "}
          <Box
            component="span"
            onClick={() => setTroubleshootOpen(true)}
            sx={{
              color: "primary.clr-300",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Troubleshoot here
          </Box>
        </Typography>
      </Box>
    </Dialog>

    <TroubleshootMicModal
      open={troubleshootOpen}
      onClose={() => setTroubleshootOpen(false)}
      granted={permState === "GRANTED"}
    />
    </>
  );
}
